import React, { useCallback, useEffect } from 'react';
import type { FC } from 'react';

import type { EditorActions } from '@atlaskit/editor-core';
import type { InlineCommentViewComponentProps } from '@atlaskit/editor-plugins/annotation';

import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter';
import { useInlineCommentQueryParams } from '@confluence/comment';
import { useIsLivePage } from '@confluence/live-pages-utils/entry-points/useIsLivePage';
import type { CommentAction } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes';
import { useAnnotationsDispatch } from '@confluence/annotation-provider-store';
import { fg } from '@confluence/feature-gating';
import { useCommentsPanel, scrollCommentsPanel } from '@confluence/comments-panel';

import { InlineComment } from '../InlineComment';

export type ViewComponentProps = {
	pageId: string;
	editorActions?: EditorActions;
};

export const editorHandleNavigationClick = (nextMarkerRef: string) => {
	const emitter = getEditorAnnotationEventEmitter();
	emitter.emit('setselectedannotation', nextMarkerRef);
};

export const ViewComponent: FC<InlineCommentViewComponentProps & ViewComponentProps> = ({
	annotations: clickedAnnotations,
	getInlineNodeTypes,
	editorActions,
	onClose,
	onDelete,
}) => {
	const { setEditorViewProps } = useAnnotationsDispatch();
	const isLivePage = useIsLivePage();

	const supportedCommentActions: CommentAction[] = !isLivePage
		? ['edit', 'resolve']
		: ['edit', 'resolve', 'delete'];

	const { removeCommentQueryParams } = useInlineCommentQueryParams();

	const [{ isCommentsPanelOpen }, { setCurrentlySelectedCommentMarkerRef }] = useCommentsPanel();
	const isCommentsPanelFGEnabled = fg('confluence-frontend-comments-panel');

	const handleResolve = () => {
		setEditorViewProps(null);
	};

	const handleDelete = useCallback(
		(id: string) => {
			setEditorViewProps(null);
			onDelete && onDelete(id);
		},
		[setEditorViewProps, onDelete],
	);

	useEffect(() => {
		const shouldScrollCommentsPanelToComment =
			isCommentsPanelFGEnabled && isCommentsPanelOpen && clickedAnnotations.length > 0;
		if (shouldScrollCommentsPanelToComment) {
			setCurrentlySelectedCommentMarkerRef(clickedAnnotations[0].id);
			scrollCommentsPanel({
				containerId: 'comments-panel-list-container',
				commentMarkerRef: `comment-thread-${clickedAnnotations[0].id}-container`,
			});
		}
	}, [
		isCommentsPanelFGEnabled,
		isCommentsPanelOpen,
		setCurrentlySelectedCommentMarkerRef,
		clickedAnnotations,
	]);

	const shouldRenderStandaloneInlineComment =
		clickedAnnotations.length > 0 && (!isCommentsPanelFGEnabled || !isCommentsPanelOpen);
	return shouldRenderStandaloneInlineComment ? (
		<InlineComment
			isEditor
			annotation={clickedAnnotations[0]}
			getInlineNodeTypes={getInlineNodeTypes}
			onClose={onClose}
			editorActions={editorActions}
			onNavigationClick={(nextMarkerRef: string) => {
				editorHandleNavigationClick(nextMarkerRef);
				removeCommentQueryParams();
			}}
			onResolve={handleResolve}
			onDelete={handleDelete}
			supportedTopLevelActions={supportedCommentActions}
		/>
	) : (
		<></>
	);
};
